import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Divider,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaSave } from "react-icons/fa";
import API from "../../context/API";
import LandingPage from "./LandingPage";
import CategoriesPage from "./CategoriesPage";
import ItemModal from "./ItemModal";
import { motion, AnimatePresence } from "framer-motion"; // Import motion from framer-motion
import StoreClosedModal from "./StoreClosedModal";
import InactivityModal from "./InActivityModal";

const MotionTabPanel = motion(TabPanel);

function Configuration() {
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    // Landing Page
    landingPageGradientColors: "#04018C,#FD3A69,#04018C",
    landingPageWelcomeTextColor: "#FFFFFF",
    landingPageWelcomeFontSize: 42,
    landingPageInstructionTextColor: "#FFFFFF",
    landingPageInstructionFontSize: 24,
    landingPageCardTextColor: "#f4c430",
    landingPageCardFontSize: 24,
    landingPageCardBorderColor: "#FFFFFF",
    landingPageCardIconColor: "#f4c430",
    landingPageSelectedCardBorderColor: "#00FF00",
    landingPageLanguageTextColor: "#C3E6F7",
    landingPageSelectedLanguageTextColor: "#FFFFFF",
    landingPageLanguageButtonBorderColor: "#C3E6F7",
    landingPageSelectedLanguageButtonBorderColor: "#00FF00",
    landingPageDeviceIdTextColor: "#FFFFFF",
    landingPageErrorTextColor: "#FF0000",
    landingPageErrorIconColor: "#FF0000",
  
    // Category Page
    categoryBackgroundColor: "#f5f5f5",
    categoryBorderColor: "#e0e0e0",
    categorySelectedBorderColor: "#ff0000",
    categoryBorderWidth: 3,
    categoryTitleColor: "#333333",
    categorySelectedBorderWidth: 6,
  
    kioskCategorySelectionColor: "#04018C",
    kioskCategoryBackgroundColor: "#FFFFFF",
    kioskCategoryForegroundColor: "#000000",
  
    itemCardBackgroundColor: "#f8f8f8",
    itemCardBorderColor: "#FFFFFF",  // Transparent as hex
    itemCardBorderWidth: 0,
    itemCardBorderRadius: 12,
    itemNameTextColor: "#000000",
    itemNameFontSize: 26,
    itemDescriptionTextColor: "#000000",
    itemDescriptionFontSize: 24,
    itemPriceTextColor: "#000000",
    itemPriceFontSize: 20,
    itemImageWidth: 150,
    itemImageHeight: 150,
    itemImageBorderRadius: 8,
    soldOutBackgroundColor: "#FF0000",  // Red background
    soldOutTextColor: "#FFFFFF",  // White text
    soldOutFontSize: 20,
    soldOutText: "SOLD OUT",
  
    // View Cart Button
    viewCartButtonBackgroundColor: "#4CAF50",
    viewCartButtonTextColor: "#FFFFFF",
    viewCartButtonFontSize: 18,
    viewCartButtonPadding: 15,
    viewCartButtonBorderRadius: 50,
    viewCartButtonShadowColor: "#000000",
  
    // Item Modal
    storeClosedModalBackgroundColor: "#FFFFFF",
    storeClosedModalBorderRadius: 10,
    storeClosedModalPadding: 20,
    storeClosedModalTextColor: "#000000",
    storeClosedModalTextSize: 40,
    storeClosedModalCloseButtonColor: "#0000FF",
    storeClosedModalCloseButtonSize: 35,
    inactivityModalTextColor: "#4a4a4a",
    inactivityModalBackgroundColor: "#FFFFFF",
    inactivityModalBorderRadius: 20,
    inactivityModalPadding: 24,
    inactivityModalTitleColor: "#1a1a1a",
    inactivityModalTitleSize: 20,
    inactivityModalTextSize: 16,
    inactivityModalButtonColor: "#4CAF50",
    inactivityModalButtonTextColor: "#FFFFFF",
    inactivityModalButtonTextSize: 16,
  
    // Discover Screen
    discoverScreenLargeTextSize: 40,
    discoverScreenTextColor: "#000000",
    discoverScreenListItemPadding: 15,
    discoverScreenListItemBorderColor: "#DDDDDD",
    discoverScreenListItemTextSize: 18,
    discoverScreenDisabledItemColor: "#AAAAAA",
    discoverScreenButtonColor: "#4CAF50",
    discoverScreenButtonTextColor: "#FFFFFF",
    discoverScreenButtonTextSize: 18,
  
    // Drawer
    drawerBackgroundColor: "#FFFFFF",
    drawerBorderRadius: 20,
    drawerCloseButtonBackgroundColor: "rgba(255, 255, 255, 0.9)",
    drawerCloseButtonBorderRadius: 20,
    drawerCloseButtonIconColor: "#000000",
    drawerItemTitleColor: "#000000",
    drawerItemTitleFontSize: 34,
    drawerItemPriceColor: "#4CAF50",
    drawerItemPriceFontSize: 30,
    drawerItemDescriptionColor: "#000000",
    drawerItemDescriptionFontSize: 20,
    drawerSectionTitleColor: "#000000",
    drawerSectionTitleFontSize: 22,
    drawerSectionDividerColor: "#DDDDDD",
    drawerSectionDividerThickness: 5,
    drawerRequiredIndicatorColor: "#FF0000",
    drawerRequiredIndicatorFontSize: 15,
    drawerOptionalIndicatorColor: "#000000",
    drawerOptionalIndicatorFontSize: 15,
    drawerSoldOutTextColor: "#FFFFFF",
    drawerSoldOutBackgroundColor: "#FF0000",
    drawerSoldOutFontSize: 20,
    drawerBottomActionsBorderColor: "#EEEEEE",
    drawerBottomActionsBackgroundColor: "#FFFFFF",
    drawerAddToCartButtonColor: "#4CAF50",
    drawerDisabledButtonColor: "#CCCCCC",
    drawerAddToCartButtonTextColor: "#FFFFFF",
    drawerAddToCartButtonTextFontSize: 18,
    drawerAddToCartButtonBorderRadius: 8,
    drawerIncrementButtonColor: "#4CAF50",
    drawerDecrementButtonColor: "#FF6347",
    drawerQuantityButtonTextColor: "#FFFFFF",
    drawerQuantityButtonTextFontSize: 24,
    drawerQuantityValueColor: "#000000",
    drawerQuantityValueFontSize: 22,
    drawerQuantityButtonSize: 40,
  });
  

  const [selectedTab, setSelectedTab] = useState(0);
  const toast = useToast();

  // Color mode values for dark mode support
  const bgColor = useColorModeValue("gray.50", "gray.800");
  const previewBgColor = useColorModeValue("gray.100", "gray.700");
  const headingColor = useColorModeValue("teal.600", "teal.300");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await API.get("/kiosk-configuration", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setConfig(response.data); // Change from response to response.data
        setFormData({
          landingPageGradientColors: response.data.landingPageGradientColors || "#04018C,#FD3A69,#04018C",
          landingPageWelcomeTextColor: response.data.landingPageWelcomeTextColor || "#FFFFFF",
          landingPageWelcomeFontSize: response.data.landingPageWelcomeFontSize || 42,
          landingPageInstructionTextColor: response.data.landingPageInstructionTextColor || "#FFFFFF",
          landingPageInstructionFontSize: response.data.landingPageInstructionFontSize || 24,
          landingPageCardTextColor: response.data.landingPageCardTextColor || "#F4C430",
          landingPageCardFontSize: response.data.landingPageCardFontSize || 24,
          landingPageCardBorderColor: response.data.landingPageCardBorderColor || "#FFFFFF",
          landingPageCardIconColor: response.data.landingPageCardIconColor || "#F4C430",
          landingPageSelectedCardBorderColor: response.data.landingPageSelectedCardBorderColor || "#00FF00",
          landingPageLanguageTextColor: response.data.landingPageLanguageTextColor || "#C3E6F7",
          landingPageSelectedLanguageTextColor: response.data.landingPageSelectedLanguageTextColor || "#FFFFFF",
          landingPageLanguageButtonBorderColor: response.data.landingPageLanguageButtonBorderColor || "#C3E6F7",
          landingPageSelectedLanguageButtonBorderColor: response.data.landingPageSelectedLanguageButtonBorderColor || "#00FF00",
          landingPageDeviceIdTextColor: response.data.landingPageDeviceIdTextColor || "#FFFFFF",
          landingPageErrorTextColor: response.data.landingPageErrorTextColor || "#FF0000",
          landingPageErrorIconColor: response.data.landingPageErrorIconColor || "#FF0000",
        
          categoryBackgroundColor: response.data.categoryBackgroundColor || "#F5F5F5",
          categoryBorderColor: response.data.categoryBorderColor || "#E0E0E0",
          categorySelectedBorderColor: response.data.categorySelectedBorderColor || "#FF0000",
          categoryBorderWidth: response.data.categoryBorderWidth || 3,
          categoryTitleColor: response.data.categoryTitleColor || "#333333",
          categorySelectedBorderWidth: response.data.categorySelectedBorderWidth || 6,
        
          kioskCategorySelectionColor: response.data.kioskCategorySelectionColor || "#04018C",
          kioskCategoryBackgroundColor: response.data.kioskCategoryBackgroundColor || "#FFFFFF",
          kioskCategoryForegroundColor: response.data.kioskCategoryForegroundColor || "#000000",
        
          itemCardBackgroundColor: response.data.itemCardBackgroundColor || "#F8F8F8",
          itemCardBorderColor: response.data.itemCardBorderColor || "#FFFFFF",
          itemCardBorderWidth: response.data.itemCardBorderWidth || 0,
          itemCardBorderRadius: response.data.itemCardBorderRadius || 12,
          itemNameTextColor: response.data.itemNameTextColor || "#000000",
          itemNameFontSize: response.data.itemNameFontSize || 26,
          itemDescriptionTextColor: response.data.itemDescriptionTextColor || "#000000",
          itemDescriptionFontSize: response.data.itemDescriptionFontSize || 24,
          itemPriceTextColor: response.data.itemPriceTextColor || "#000000",
          itemPriceFontSize: response.data.itemPriceFontSize || 20,
          itemImageWidth: response.data.itemImageWidth || 150,
          itemImageHeight: response.data.itemImageHeight || 150,
          itemImageBorderRadius: response.data.itemImageBorderRadius || 8,
          soldOutBackgroundColor: response.data.soldOutBackgroundColor || "#FF0000",
          soldOutTextColor: response.data.soldOutTextColor || "#FFFFFF",
          soldOutFontSize: response.data.soldOutFontSize || 20,
          soldOutText: response.data.soldOutText || "SOLD OUT",
        
          viewCartButtonBackgroundColor: response.data.viewCartButtonBackgroundColor || "#4CAF50",
          viewCartButtonTextColor: response.data.viewCartButtonTextColor || "#FFFFFF",
          viewCartButtonFontSize: response.data.viewCartButtonFontSize || 18,
          viewCartButtonPadding: response.data.viewCartButtonPadding || 15,
          viewCartButtonBorderRadius: response.data.viewCartButtonBorderRadius || 50,
          viewCartButtonShadowColor: response.data.viewCartButtonShadowColor || "#000000",
        
          storeClosedModalBackgroundColor: response.data.storeClosedModalBackgroundColor || "#FFFFFF",
          storeClosedModalBorderRadius: response.data.storeClosedModalBorderRadius || 10,
          storeClosedModalPadding: response.data.storeClosedModalPadding || 20,
          storeClosedModalTextColor: response.data.storeClosedModalTextColor || "#000000",
          storeClosedModalTextSize: response.data.storeClosedModalTextSize || 40,
          storeClosedModalCloseButtonColor: response.data.storeClosedModalCloseButtonColor || "#0000FF",
          storeClosedModalCloseButtonSize: response.data.storeClosedModalCloseButtonSize || 35,
        
          inactivityModalTextColor: response.data.inactivityModalTextColor || "#4A4A4A",
          inactivityModalBackgroundColor: response.data.inactivityModalBackgroundColor || "#FFFFFF",
          inactivityModalBorderRadius: response.data.inactivityModalBorderRadius || 20,
          inactivityModalPadding: response.data.inactivityModalPadding || 24,
          inactivityModalTitleColor: response.data.inactivityModalTitleColor || "#1A1A1A",
          inactivityModalTitleSize: response.data.inactivityModalTitleSize || 20,
          inactivityModalTextSize: response.data.inactivityModalTextSize || 16,
          inactivityModalButtonColor: response.data.inactivityModalButtonColor || "#4CAF50",
          inactivityModalButtonTextColor: response.data.inactivityModalButtonTextColor || "#FFFFFF",
          inactivityModalButtonTextSize: response.data.inactivityModalButtonTextSize || 16,
        
          discoverScreenLargeTextSize: response.data.discoverScreenLargeTextSize || 40,
          discoverScreenTextColor: response.data.discoverScreenTextColor || "#000000",
          discoverScreenListItemPadding: response.data.discoverScreenListItemPadding || 15,
          discoverScreenListItemBorderColor: response.data.discoverScreenListItemBorderColor || "#DDDDDD",
          discoverScreenListItemTextSize: response.data.discoverScreenListItemTextSize || 18,
          discoverScreenDisabledItemColor: response.data.discoverScreenDisabledItemColor || "#AAAAAA",
          discoverScreenButtonColor: response.data.discoverScreenButtonColor || "#4CAF50",
          discoverScreenButtonTextColor: response.data.discoverScreenButtonTextColor || "#FFFFFF",
          discoverScreenButtonTextSize: response.data.discoverScreenButtonTextSize || 18,
        
          drawerBackgroundColor: response.data.drawerBackgroundColor || "#FFFFFF",
          drawerBorderRadius: response.data.drawerBorderRadius || 20,
          drawerCloseButtonBackgroundColor: response.data.drawerCloseButtonBackgroundColor || "rgba(255, 255, 255, 0.9)",
          drawerCloseButtonBorderRadius: response.data.drawerCloseButtonBorderRadius || 20,
          drawerCloseButtonIconColor: response.data.drawerCloseButtonIconColor || "#000000",
          drawerItemTitleColor: response.data.drawerItemTitleColor || "#000000",
          drawerItemTitleFontSize: response.data.drawerItemTitleFontSize || 34,
          drawerItemPriceColor: response.data.drawerItemPriceColor || "#4CAF50",
          drawerItemPriceFontSize: response.data.drawerItemPriceFontSize || 30,
          drawerItemDescriptionColor: response.data.drawerItemDescriptionColor || "#000000",
          drawerItemDescriptionFontSize: response.data.drawerItemDescriptionFontSize || 20,
          drawerSectionTitleColor: response.data.drawerSectionTitleColor || "#000000",
          drawerSectionTitleFontSize: response.data.drawerSectionTitleFontSize || 22,
          drawerSectionDividerColor: response.data.drawerSectionDividerColor || "#DDDDDD",
          drawerSectionDividerThickness: response.data.drawerSectionDividerThickness || 5,
          drawerRequiredIndicatorColor: response.data.drawerRequiredIndicatorColor || "#FF0000",
          drawerRequiredIndicatorFontSize: response.data.drawerRequiredIndicatorFontSize || 15,
          drawerOptionalIndicatorColor: response.data.drawerOptionalIndicatorColor || "#000000",
          drawerOptionalIndicatorFontSize: response.data.drawerOptionalIndicatorFontSize || 15,
          drawerSoldOutTextColor: response.data.drawerSoldOutTextColor || "#FFFFFF",
          drawerSoldOutBackgroundColor: response.data.drawerSoldOutBackgroundColor || "#FF0000",
          drawerSoldOutFontSize: response.data.drawerSoldOutFontSize || 20,
          drawerBottomActionsBorderColor: response.data.drawerBottomActionsBorderColor || "#EEEEEE",
          drawerBottomActionsBackgroundColor: response.data.drawerBottomActionsBackgroundColor || "#FFFFFF",
          drawerAddToCartButtonColor: response.data.drawerAddToCartButtonColor || "#4CAF50",
          drawerDisabledButtonColor: response.data.drawerDisabledButtonColor || "#CCCCCC",
          drawerAddToCartButtonTextColor: response.data.drawerAddToCartButtonTextColor || "#FFFFFF",
          drawerAddToCartButtonTextFontSize: response.data.drawerAddToCartButtonTextFontSize || 18,
          drawerAddToCartButtonBorderRadius: response.data.drawerAddToCartButtonBorderRadius || 8,
          drawerIncrementButtonColor: response.data.drawerIncrementButtonColor || "#4CAF50",
          drawerDecrementButtonColor: response.data.drawerDecrementButtonColor || "#FF6347",
          drawerQuantityButtonTextColor: response.data.drawerQuantityButtonTextColor || "#FFFFFF",
          drawerQuantityButtonTextFontSize: response.data.drawerQuantityButtonTextFontSize || 24,
          drawerQuantityValueColor: response.data.drawerQuantityValueColor || "#000000",
          drawerQuantityValueFontSize: response.data.drawerQuantityValueFontSize || 22,
          drawerQuantityButtonSize: response.data.drawerQuantityButtonSize || 40,
        });
        
      } catch (error) {
        console.error("Error fetching configuration:", error);
        toast({
          title: "Error fetching configuration.",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchConfig();
  }, [toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (
      name === "landingPageGradientColor1" ||
      name === "landingPageGradientColor2" ||
      name === "landingPageGradientColor3"
    ) {
      // Add null check to prevent split() error
      const currentGradient = formData.landingPageGradientColors || "#04018C,#FD3A69,#04018C";
      const updatedGradient = currentGradient.split(",");
      updatedGradient[
        name === "landingPageGradientColor1"
          ? 0
          : name === "landingPageGradientColor2"
          ? 1
          : 2
      ] = value;

      setFormData((prev) => ({
        ...prev,
        // Don't set individual gradient properties to avoid API issues
        // [name]: value, // Remove this line
        landingPageGradientColors: updatedGradient.join(","),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = async () => {
    try {
      // Validate and convert color values to hex format
      const validatedFormData = { ...formData };
      
      // Remove unwanted properties that the API doesn't accept
      delete validatedFormData.landingPageGradientColor1;
      delete validatedFormData.landingPageGradientColor2;
      delete validatedFormData.landingPageGradientColor3;
      
      // List of color fields that must be hex
      const colorFields = [
        'landingPageWelcomeTextColor',
        'landingPageInstructionTextColor',
        'landingPageCardTextColor',
        'landingPageCardBorderColor',
        'landingPageCardIconColor',
        'landingPageSelectedCardBorderColor',
        'landingPageLanguageTextColor',
        'landingPageSelectedLanguageTextColor',
        'landingPageLanguageButtonBorderColor',
        'landingPageSelectedLanguageButtonBorderColor',
        'landingPageDeviceIdTextColor',
        'landingPageErrorTextColor',
        'landingPageErrorIconColor',
        'categoryBackgroundColor',
        'categoryBorderColor',
        'categorySelectedBorderColor',
        'categoryTitleColor',
        'kioskCategorySelectionColor',
        'kioskCategoryBackgroundColor',
        'kioskCategoryForegroundColor',
        'itemCardBackgroundColor',
        'itemCardBorderColor',
        'itemNameTextColor',
        'itemDescriptionTextColor',
        'itemPriceTextColor',
        'soldOutBackgroundColor',
        'soldOutTextColor',
        'viewCartButtonBackgroundColor',
        'viewCartButtonTextColor',
        'viewCartButtonShadowColor',
        'storeClosedModalBackgroundColor',
        'storeClosedModalTextColor',
        'storeClosedModalCloseButtonColor',
        'inactivityModalTextColor',
        'inactivityModalBackgroundColor',
        'inactivityModalTitleColor',
        'inactivityModalButtonColor',
        'inactivityModalButtonTextColor',
        'discoverScreenTextColor',
        'discoverScreenListItemBorderColor',
        'discoverScreenDisabledItemColor',
        'discoverScreenButtonColor',
        'discoverScreenButtonTextColor',
        'drawerCloseButtonIconColor',
        'drawerItemTitleColor',
        'drawerItemPriceColor',
        'drawerItemDescriptionColor',
        'drawerSectionTitleColor',
        'drawerSectionDividerColor',
        'drawerRequiredIndicatorColor',
        'drawerOptionalIndicatorColor',
        'drawerSoldOutTextColor',
        'drawerSoldOutBackgroundColor',
        'drawerBottomActionsBorderColor',
        'drawerBottomActionsBackgroundColor',
        'drawerAddToCartButtonColor',
        'drawerDisabledButtonColor',
        'drawerAddToCartButtonTextColor',
        'drawerIncrementButtonColor',
        'drawerDecrementButtonColor',
        'drawerQuantityButtonTextColor',
        'drawerQuantityValueColor'
      ];

      // Convert color names to hex codes
      const colorNameToHex = {
        'red': '#FF0000',
        'white': '#FFFFFF',
        'black': '#000000',
        'transparent': '#FFFFFF', // or handle transparent differently
        'fff': '#FFFFFF',
        '000': '#000000'
      };

      // Validate and convert colors
      colorFields.forEach(field => {
        if (validatedFormData[field]) {
          const colorValue = validatedFormData[field];
          if (colorNameToHex[colorValue.toLowerCase()]) {
            validatedFormData[field] = colorNameToHex[colorValue.toLowerCase()];
          } else if (!colorValue.startsWith('#')) {
            // If it's not a hex code and not a known color name, default to black
            validatedFormData[field] = '#000000';
          }
        }
      });

      // Additional validation for specific fields
      if (validatedFormData.kioskCategorySelectionColor && validatedFormData.kioskCategorySelectionColor.startsWith('"')) {
        // Remove extra quotes if present
        validatedFormData.kioskCategorySelectionColor = validatedFormData.kioskCategorySelectionColor.replace(/"/g, '');
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("access_token");
      if (user.role === "merchant") {
         await API.patch(
          `/kiosk-configuration`,
          validatedFormData, // Use validated data instead of formData
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      setConfig(validatedFormData);
      toast({
        title: "Configuration updated.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Configuration save error:", error);
      toast({
        title: "Error updating configuration.",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!config) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Flex direction="row" padding="4" gap="6" height="100vh" overflow="auto">
      {/* Configuration Form */}
      <Box
        flex="1.5"
        padding="6"
        boxShadow="md"
        borderRadius="lg"
        bg={bgColor}
        overflowY={"auto"}
      >
        <Heading size="lg" marginBottom="6" textAlign="center" color={headingColor}>
          Kiosk Design Configuration
        </Heading>
        <Tabs index={selectedTab} onChange={(index) => setSelectedTab(index)}>
          {/* <Tabs isLazy variant="enclosed-colored" colorScheme="teal" onChange={handleTabChange}> */}
          <TabList
            display="flex"
            justifyContent="center"
            gap={4}
            borderBottom="2px solid"
            borderColor={borderColor}
            py={3}
            mb={6}
          >
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "bold",
                borderRadius: "10px",
              }}
              _hover={{
                bg: "teal.400",
                color: "white",
              }}
              px={6}
              py={2}
              fontSize="md"
              borderRadius="lg"
              fontWeight="semibold"
            >
              Landing Page
            </Tab>
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "bold",
                borderRadius: "10px",
              }}
              _hover={{
                bg: "teal.400",
                color: "white",
              }}
              px={6}
              py={2}
              fontSize="md"
              borderRadius="lg"
              fontWeight="semibold"
            >
              Categories Page
            </Tab>
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "bold",
                borderRadius: "10px",
              }}
              _hover={{
                bg: "teal.400",
                color: "white",
              }}
              px={6}
              py={2}
              fontSize="md"
              borderRadius="lg"
              fontWeight="semibold"
            >
              Drawer
            </Tab>
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "bold",
                borderRadius: "10px",
              }}
              _hover={{
                bg: "teal.400",
                color: "white",
              }}
              px={6}
              py={2}
              fontSize="md"
              borderRadius="lg"
              fontWeight="semibold"
            >
              Store Closed Modal
            </Tab>
            <Tab
              _selected={{
                color: "white",
                bg: "teal.500",
                fontWeight: "bold",
                borderRadius: "10px",
              }}
              _hover={{
                bg: "teal.400",
                color: "white",
              }}
              px={6}
              py={2}
              fontSize="md"
              borderRadius="lg"
              fontWeight="semibold"
            >
              In-Activity Modal
            </Tab>
          </TabList>

          <TabPanels>
            {/* Landing Page Tab */}
            <TabPanel>
              <VStack spacing="6" align="stretch">
                {/* Gradient Colors */}
                <FormControl>
                  <FormLabel>Landing Page Gradient Color 1</FormLabel>
                  <Input
                    type="color"
                    name="landingPageGradientColor1"
                    value={formData.landingPageGradientColors.split(",")[0]}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Landing Page Gradient Color 2</FormLabel>
                  <Input
                    type="color"
                    name="landingPageGradientColor2"
                    value={formData.landingPageGradientColors.split(",")[1]}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Landing Page Gradient Color 3</FormLabel>
                  <Input
                    type="color"
                    name="landingPageGradientColor3"
                    value={formData.landingPageGradientColors.split(",")[2]}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Welcome Text Color */}
                <FormControl>
                  <FormLabel>Welcome Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageWelcomeTextColor"
                    value={formData.landingPageWelcomeTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Welcome Text Font Size */}
                <FormControl>
                  <FormLabel>Welcome Text Font Size</FormLabel>
                  <Slider
                    min={8}
                    max={55}
                    value={formData.landingPageWelcomeFontSize}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        landingPageWelcomeFontSize: value,
                      }))
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="sm" color={headingColor}>
                    {formData.landingPageWelcomeFontSize}px
                  </Text>
                </FormControl>

                {/* Instruction Text Color */}
                <FormControl>
                  <FormLabel>Instruction Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageInstructionTextColor"
                    value={formData.landingPageInstructionTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Instruction Text Font Size */}
                <FormControl>
                  <FormLabel>Instruction Text Font Size</FormLabel>
                  <Slider
                    min={8}
                    max={30}
                    value={formData.landingPageInstructionFontSize}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        landingPageInstructionFontSize: value,
                      }))
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <Text fontSize="sm" color={headingColor}>
                    {formData.landingPageInstructionFontSize}px
                  </Text>
                </FormControl>

                {/* Card Text Color */}
                <FormControl>
                  <FormLabel>Card Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageCardTextColor"
                    value={formData.landingPageCardTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Card Font Size */}
                <FormControl>
                  <FormLabel>Card Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.landingPageCardFontSize}
                    min={12}
                    max={36}
                    step={1}
                    value={formData.landingPageCardFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "landingPageCardFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Card Border Color */}
                <FormControl>
                  <FormLabel>Card Border Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageCardBorderColor"
                    value={formData.landingPageCardBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Card Icon Color */}
                <FormControl>
                  <FormLabel>Card Icon Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageCardIconColor"
                    value={formData.landingPageCardIconColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Selected Card Border Color */}
                <FormControl>
                  <FormLabel>Selected Card Border Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageSelectedCardBorderColor"
                    value={formData.landingPageSelectedCardBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Language Text Color */}
                <FormControl>
                  <FormLabel>Language Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageLanguageTextColor"
                    value={formData.landingPageLanguageTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Selected Language Text Color */}
                <FormControl>
                  <FormLabel>Selected Language Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageSelectedLanguageTextColor"
                    value={formData.landingPageSelectedLanguageTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Language Button Border Color */}
                <FormControl>
                  <FormLabel>Language Button Border Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageLanguageButtonBorderColor"
                    value={formData.landingPageLanguageButtonBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Selected Language Button Border Color */}
                <FormControl>
                  <FormLabel>Selected Language Button Border Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageSelectedLanguageButtonBorderColor"
                    value={
                      formData.landingPageSelectedLanguageButtonBorderColor
                    }
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Device ID Text Color */}
                <FormControl>
                  <FormLabel>Device ID Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageDeviceIdTextColor"
                    value={formData.landingPageDeviceIdTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Error Text Color */}
                <FormControl>
                  <FormLabel>Error Text Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageErrorTextColor"
                    value={formData.landingPageErrorTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Error Icon Color */}
                <FormControl>
                  <FormLabel>Error Icon Color</FormLabel>
                  <Input
                    type="color"
                    name="landingPageErrorIconColor"
                    value={formData.landingPageErrorIconColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>
              </VStack>
            </TabPanel>

            {/* Categories Page Tab */}
            <TabPanel>
              <VStack spacing="6" align="stretch">
                <FormControl>
                  <FormLabel>Category Background Color</FormLabel>
                  <Input
                    type="color"
                    name="categoryBackgroundColor"
                    value={formData.categoryBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Category Border Color</FormLabel>
                  <Input
                    type="color"
                    name="categoryBorderColor"
                    value={formData.categoryBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Category Selected Border Color</FormLabel>
                  <Input
                    type="color"
                    name="categorySelectedBorderColor"
                    value={formData.categorySelectedBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Category Border Width</FormLabel>
                  <Slider
                    defaultValue={formData.categoryBorderWidth}
                    min={1}
                    max={10}
                    step={1}
                    value={formData.categoryBorderWidth}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "categoryBorderWidth", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                <FormControl>
                  <FormLabel>View Cart Button Background Color</FormLabel>
                  <Input
                    type="color"
                    name="viewCartButtonBackgroundColor"
                    value={formData.viewCartButtonBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>View Cart Button Text Color</FormLabel>
                  <Input
                    type="color"
                    name="viewCartButtonTextColor"
                    value={formData.viewCartButtonTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>View Cart Button Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.viewCartButtonFontSize}
                    min={12}
                    max={36}
                    step={1}
                    value={formData.viewCartButtonFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "viewCartButtonFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Add Category Title Color */}
                <FormControl>
                  <FormLabel>Category Title Color</FormLabel>
                  <Input
                    type="color"
                    name="categoryTitleColor"
                    value={formData.categoryTitleColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Add Category Selected Border Width */}
                <FormControl>
                  <FormLabel>Category Selected Border Width</FormLabel>
                  <Slider
                    defaultValue={formData.categorySelectedBorderWidth}
                    min={1}
                    max={10}
                    step={1}
                    value={formData.categorySelectedBorderWidth}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "categorySelectedBorderWidth", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Kiosk Category Selection Color */}
                <FormControl>
                  <FormLabel>Kiosk Category Selection Color</FormLabel>
                  <Input
                    type="color"
                    name="kioskCategorySelectionColor"
                    value={formData.kioskCategorySelectionColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Kiosk Category Background Color */}
                <FormControl>
                  <FormLabel>Kiosk Category Background Color</FormLabel>
                  <Input
                    type="color"
                    name="kioskCategoryBackgroundColor"
                    value={formData.kioskCategoryBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Kiosk Category Foreground Color */}
                <FormControl>
                  <FormLabel>Kiosk Category Foreground Color</FormLabel>
                  <Input
                    type="color"
                    name="kioskCategoryForegroundColor"
                    value={formData.kioskCategoryForegroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Item Card Background Color */}
                <FormControl>
                  <FormLabel>Item Card Background Color</FormLabel>
                  <Input
                    type="color"
                    name="itemCardBackgroundColor"
                    value={formData.itemCardBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Item Card Border Color */}
                <FormControl>
                  <FormLabel>Item Card Border Color</FormLabel>
                  <Input
                    type="color"
                    name="itemCardBorderColor"
                    value={formData.itemCardBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Item Card Border Width */}
                <FormControl>
                  <FormLabel>Item Card Border Width</FormLabel>
                  <Slider
                    defaultValue={formData.itemCardBorderWidth}
                    min={1}
                    max={10}
                    step={1}
                    value={formData.itemCardBorderWidth}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemCardBorderWidth", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Item Card Border Radius */}
                <FormControl>
                  <FormLabel>Item Card Border Radius</FormLabel>
                  <Slider
                    defaultValue={formData.itemCardBorderRadius}
                    min={1}
                    max={20}
                    step={1}
                    value={formData.itemCardBorderRadius}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemCardBorderRadius", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Item Name Text Color */}
                <FormControl>
                  <FormLabel>Item Name Text Color</FormLabel>
                  <Input
                    type="color"
                    name="itemNameTextColor"
                    value={formData.itemNameTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Item Name Font Size */}
                <FormControl>
                  <FormLabel>Item Name Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.itemNameFontSize}
                    min={10}
                    max={40}
                    step={1}
                    value={formData.itemNameFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemNameFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Item Description Text Color */}
                <FormControl>
                  <FormLabel>Item Description Text Color</FormLabel>
                  <Input
                    type="color"
                    name="itemDescriptionTextColor"
                    value={formData.itemDescriptionTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Item Description Font Size */}
                <FormControl>
                  <FormLabel>Item Description Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.itemDescriptionFontSize}
                    min={10}
                    max={40}
                    step={1}
                    value={formData.itemDescriptionFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemDescriptionFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Item Price Text Color */}
                <FormControl>
                  <FormLabel>Item Price Text Color</FormLabel>
                  <Input
                    type="color"
                    name="itemPriceTextColor"
                    value={formData.itemPriceTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Item Price Font Size */}
                <FormControl>
                  <FormLabel>Item Price Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.itemPriceFontSize}
                    min={12}
                    max={30}
                    step={1}
                    value={formData.itemPriceFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemPriceFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Item Image Width */}
                <FormControl>
                  <FormLabel>Item Image Width</FormLabel>
                  <Slider
                    defaultValue={formData.itemImageWidth}
                    min={50}
                    max={300}
                    step={5}
                    value={formData.itemImageWidth}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemImageWidth", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Item Image Height */}
                <FormControl>
                  <FormLabel>Item Image Height</FormLabel>
                  <Slider
                    defaultValue={formData.itemImageHeight}
                    min={50}
                    max={300}
                    step={5}
                    value={formData.itemImageHeight}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "itemImageHeight", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>
              </VStack>
            </TabPanel>

            {/* Item Modal Tab */}
            <TabPanel>
              <VStack spacing="6" align="stretch">
                {/* Drawer Background Color */}
                <FormControl>
                  <FormLabel>Drawer Background Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerBackgroundColor"
                    value={formData.drawerBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Border Radius */}
                <FormControl>
                  <FormLabel>Drawer Border Radius</FormLabel>
                  <Slider
                    defaultValue={formData.drawerBorderRadius}
                    min={0}
                    max={50}
                    step={1}
                    value={formData.drawerBorderRadius}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerBorderRadius", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Close Button Background Color */}
                <FormControl>
                  <FormLabel>Drawer Close Button Background Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerCloseButtonBackgroundColor"
                    value={formData.drawerCloseButtonBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Close Button Border Radius */}
                <FormControl>
                  <FormLabel>Drawer Close Button Border Radius</FormLabel>
                  <Slider
                    defaultValue={formData.drawerCloseButtonBorderRadius}
                    min={0}
                    max={50}
                    step={1}
                    value={formData.drawerCloseButtonBorderRadius}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerCloseButtonBorderRadius",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Close Button Icon Color */}
                <FormControl>
                  <FormLabel>Drawer Close Button Icon Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerCloseButtonIconColor"
                    value={formData.drawerCloseButtonIconColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Item Title Color */}
                <FormControl>
                  <FormLabel>Drawer Item Title Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerItemTitleColor"
                    value={formData.drawerItemTitleColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Item Title Font Size */}
                <FormControl>
                  <FormLabel>Drawer Item Title Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerItemTitleFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerItemTitleFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerItemTitleFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Item Price Color */}
                <FormControl>
                  <FormLabel>Drawer Item Price Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerItemPriceColor"
                    value={formData.drawerItemPriceColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Item Price Font Size */}
                <FormControl>
                  <FormLabel>Drawer Item Price Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerItemPriceFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerItemPriceFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerItemPriceFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Item Description Color */}
                <FormControl>
                  <FormLabel>Drawer Item Description Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerItemDescriptionColor"
                    value={formData.drawerItemDescriptionColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Item Description Font Size */}
                <FormControl>
                  <FormLabel>Drawer Item Description Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerItemDescriptionFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerItemDescriptionFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerItemDescriptionFontSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Section Title Color */}
                <FormControl>
                  <FormLabel>Drawer Section Title Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerSectionTitleColor"
                    value={formData.drawerSectionTitleColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Section Title Font Size */}
                <FormControl>
                  <FormLabel>Drawer Section Title Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerSectionTitleFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerSectionTitleFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerSectionTitleFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Section Divider Color */}
                <FormControl>
                  <FormLabel>Drawer Section Divider Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerSectionDividerColor"
                    value={formData.drawerSectionDividerColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Section Divider Thickness */}
                <FormControl>
                  <FormLabel>Drawer Section Divider Thickness</FormLabel>
                  <Slider
                    defaultValue={formData.drawerSectionDividerThickness}
                    min={1}
                    max={10}
                    step={1}
                    value={formData.drawerSectionDividerThickness}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerSectionDividerThickness",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Required Indicator Color */}
                <FormControl>
                  <FormLabel>Drawer Required Indicator Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerRequiredIndicatorColor"
                    value={formData.drawerRequiredIndicatorColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Required Indicator Font Size */}
                <FormControl>
                  <FormLabel>Drawer Required Indicator Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerRequiredIndicatorFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerRequiredIndicatorFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerRequiredIndicatorFontSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Optional Indicator Color */}
                {/* <FormControl>
                  <FormLabel>Drawer Optional Indicator Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerOptionalIndicatorColor"
                    value={formData.drawerOptionalIndicatorColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl> */}

                {/* Drawer Optional Indicator Font Size */}
                {/* <FormControl>
                  <FormLabel>Drawer Optional Indicator Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerOptionalIndicatorFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerOptionalIndicatorFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerOptionalIndicatorFontSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl> */}

                {/* Drawer Sold Out Text Color */}
                {/* <FormControl>
                  <FormLabel>Drawer Sold Out Text Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerSoldOutTextColor"
                    value={formData.drawerSoldOutTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl> */}

                {/* Drawer Sold Out Background Color */}
                {/* <FormControl>
                  <FormLabel>Drawer Sold Out Background Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerSoldOutBackgroundColor"
                    value={formData.drawerSoldOutBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl> */}

                {/* Drawer Sold Out Font Size */}
                {/* <FormControl>
                  <FormLabel>Drawer Sold Out Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerSoldOutFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerSoldOutFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerSoldOutFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl> */}

                {/* Drawer Bottom Actions Border Color */}
                {/* <FormControl>
                  <FormLabel>Drawer Bottom Actions Border Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerBottomActionsBorderColor"
                    value={formData.drawerBottomActionsBorderColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl> */}

                {/* Drawer Bottom Actions Background Color */}
                {/* <FormControl>
                  <FormLabel>Drawer Bottom Actions Background Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerBottomActionsBackgroundColor"
                    value={formData.drawerBottomActionsBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl> */}

                {/* Drawer Add To Cart Button Color */}
                <FormControl>
                  <FormLabel>Drawer Add To Cart Button Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerAddToCartButtonColor"
                    value={formData.drawerAddToCartButtonColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Disabled Button Color */}
                <FormControl>
                  <FormLabel>Drawer Disabled Button Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerDisabledButtonColor"
                    value={formData.drawerDisabledButtonColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Add To Cart Button Text Color */}
                <FormControl>
                  <FormLabel>Drawer Add To Cart Button Text Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerAddToCartButtonTextColor"
                    value={formData.drawerAddToCartButtonTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Add To Cart Button Text Font Size */}
                <FormControl>
                  <FormLabel>
                    Drawer Add To Cart Button Text Font Size
                  </FormLabel>
                  <Slider
                    defaultValue={formData.drawerAddToCartButtonTextFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerAddToCartButtonTextFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerAddToCartButtonTextFontSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Add To Cart Button Border Radius */}
                <FormControl>
                  <FormLabel>Drawer Add To Cart Button Border Radius</FormLabel>
                  <Slider
                    defaultValue={formData.drawerAddToCartButtonBorderRadius}
                    min={0}
                    max={50}
                    step={1}
                    value={formData.drawerAddToCartButtonBorderRadius}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerAddToCartButtonBorderRadius",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Increment Button Color */}
                <FormControl>
                  <FormLabel>Drawer Increment Button Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerIncrementButtonColor"
                    value={formData.drawerIncrementButtonColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Decrement Button Color */}
                <FormControl>
                  <FormLabel>Drawer Decrement Button Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerDecrementButtonColor"
                    value={formData.drawerDecrementButtonColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Quantity Button Text Color */}
                <FormControl>
                  <FormLabel>Drawer Quantity Button Text Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerQuantityButtonTextColor"
                    value={formData.drawerQuantityButtonTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Quantity Button Text Font Size */}
                <FormControl>
                  <FormLabel>Drawer Quantity Button Text Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerQuantityButtonTextFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerQuantityButtonTextFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "drawerQuantityButtonTextFontSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Quantity Value Color */}
                <FormControl>
                  <FormLabel>Drawer Quantity Value Color</FormLabel>
                  <Input
                    type="color"
                    name="drawerQuantityValueColor"
                    value={formData.drawerQuantityValueColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Drawer Quantity Value Font Size */}
                <FormControl>
                  <FormLabel>Drawer Quantity Value Font Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerQuantityValueFontSize}
                    min={8}
                    max={36}
                    step={1}
                    value={formData.drawerQuantityValueFontSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerQuantityValueFontSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Drawer Quantity Button Size */}
                <FormControl>
                  <FormLabel>Drawer Quantity Button Size</FormLabel>
                  <Slider
                    defaultValue={formData.drawerQuantityButtonSize}
                    min={20}
                    max={100}
                    step={1}
                    value={formData.drawerQuantityButtonSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "drawerQuantityButtonSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>
              </VStack>
            </TabPanel>
            <TabPanel>
              <VStack spacing="6" align="stretch">
                {/* Store Closed Modal Background Color */}
                <FormControl>
                  <FormLabel>Store Closed Modal Background Color</FormLabel>
                  <Input
                    type="color"
                    name="storeClosedModalBackgroundColor"
                    value={formData.storeClosedModalBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Store Closed Modal Border Radius */}
                <FormControl>
                  <FormLabel>Store Closed Modal Border Radius</FormLabel>
                  <Slider
                    min={0}
                    max={50}
                    value={formData.storeClosedModalBorderRadius}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "storeClosedModalBorderRadius", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Store Closed Modal Padding */}
                <FormControl>
                  <FormLabel>Store Closed Modal Padding</FormLabel>
                  <Slider
                    min={0}
                    max={50}
                    value={formData.storeClosedModalPadding}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "storeClosedModalPadding", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Store Closed Modal Text Color */}
                <FormControl>
                  <FormLabel>Store Closed Modal Text Color</FormLabel>
                  <Input
                    type="color"
                    name="storeClosedModalTextColor"
                    value={formData.storeClosedModalTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Store Closed Modal Text Size */}
                <FormControl>
                  <FormLabel>Store Closed Modal Text Size</FormLabel>
                  <Slider
                    min={10}
                    max={80}
                    value={formData.storeClosedModalTextSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "storeClosedModalTextSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Store Closed Modal Close Button Color */}
                <FormControl>
                  <FormLabel>Store Closed Modal Close Button Color</FormLabel>
                  <Input
                    type="color"
                    name="storeClosedModalCloseButtonColor"
                    value={formData.storeClosedModalCloseButtonColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Store Closed Modal Close Button Size */}
                <FormControl>
                  <FormLabel>Store Closed Modal Close Button Size</FormLabel>
                  <Slider
                    min={12}
                    max={60}
                    value={formData.storeClosedModalCloseButtonSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "storeClosedModalCloseButtonSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>
              </VStack>
            </TabPanel>
            <TabPanel>
              <VStack spacing="6" align="stretch">
                {/* Inactivity Modal Text Color */}
                <FormControl>
                  <FormLabel>Inactivity Modal Text Color</FormLabel>
                  <Input
                    type="color"
                    name="inactivityModalTextColor"
                    value={formData.inactivityModalTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Inactivity Modal Background Color */}
                <FormControl>
                  <FormLabel>Inactivity Modal Background Color</FormLabel>
                  <Input
                    type="color"
                    name="inactivityModalBackgroundColor"
                    value={formData.inactivityModalBackgroundColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Inactivity Modal Border Radius */}
                <FormControl>
                  <FormLabel>Inactivity Modal Border Radius</FormLabel>
                  <Slider
                    min={0}
                    max={50}
                    value={formData.inactivityModalBorderRadius}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "inactivityModalBorderRadius", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Inactivity Modal Padding */}
                <FormControl>
                  <FormLabel>Inactivity Modal Padding</FormLabel>
                  <Slider
                    min={0}
                    max={50}
                    value={formData.inactivityModalPadding}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "inactivityModalPadding", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Inactivity Modal Title Color */}
                <FormControl>
                  <FormLabel>Inactivity Modal Title Color</FormLabel>
                  <Input
                    type="color"
                    name="inactivityModalTitleColor"
                    value={formData.inactivityModalTitleColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Inactivity Modal Title Size */}
                <FormControl>
                  <FormLabel>Inactivity Modal Title Size</FormLabel>
                  <Slider
                    min={10}
                    max={40}
                    value={formData.inactivityModalTitleSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "inactivityModalTitleSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Inactivity Modal Text Size */}
                <FormControl>
                  <FormLabel>Inactivity Modal Text Size</FormLabel>
                  <Slider
                    min={10}
                    max={30}
                    value={formData.inactivityModalTextSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "inactivityModalTextSize", value },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>

                {/* Inactivity Modal Button Color */}
                <FormControl>
                  <FormLabel>Inactivity Modal Button Color</FormLabel>
                  <Input
                    type="color"
                    name="inactivityModalButtonColor"
                    value={formData.inactivityModalButtonColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Inactivity Modal Button Text Color */}
                <FormControl>
                  <FormLabel>Inactivity Modal Button Text Color</FormLabel>
                  <Input
                    type="color"
                    name="inactivityModalButtonTextColor"
                    value={formData.inactivityModalButtonTextColor}
                    onChange={handleInputChange}
                    size="sm"
                    maxWidth="60px"
                  />
                </FormControl>

                {/* Inactivity Modal Button Text Size */}
                <FormControl>
                  <FormLabel>Inactivity Modal Button Text Size</FormLabel>
                  <Slider
                    min={12}
                    max={30}
                    value={formData.inactivityModalButtonTextSize}
                    onChange={(value) =>
                      handleInputChange({
                        target: {
                          name: "inactivityModalButtonTextSize",
                          value,
                        },
                      })
                    }
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </FormControl>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Save Button */}
        <Button
          colorScheme="teal"
          onClick={handleSave}
          isFullWidth
          leftIcon={<FaSave />}
        >
          Save Configuration
        </Button>
      </Box>

      {/* Preview */}
      <Box
        display={"flex"}
        justifyContent={"center"}
        flexDirection={"column"}
        padding="6"
        boxShadow="lg"
        borderRadius="lg"
        bg={previewBgColor}
        height={"100%"}
        width={"30%"}
      >
        <Heading size="md" marginBottom="1" textAlign="center" color={headingColor}>
          Mobile Layout Preview
        </Heading>
        <Tabs
          index={selectedTab}
          onChange={(index) => setSelectedTab(index)}
          variant="enclosed-colored"
          colorScheme="teal"
        >
          <TabPanels>
            <AnimatePresence>
              {selectedTab === 0 && (
                <motion.div
                  key="landingPage"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <LandingPage
                    gradientColors={formData.landingPageGradientColors}
                    instructionFontSize={
                      formData.landingPageInstructionFontSize
                    }
                    instructionTextColor={
                      formData.landingPageInstructionTextColor
                    }
                    welcomeFontSize={formData.landingPageWelcomeFontSize}
                    welcomeTextColor={formData.landingPageWelcomeTextColor}
                    landingPageCardTextColor={formData.landingPageCardTextColor}
                    landingPageCardFontSize={formData.landingPageCardFontSize}
                    landingPageCardBorderColor={
                      formData.landingPageCardBorderColor
                    }
                    landingPageCardIconColor={formData.landingPageCardIconColor}
                    landingPageSelectedCardBorderColor={
                      formData.landingPageSelectedCardBorderColor
                    }
                    landingPageLanguageTextColor={
                      formData.landingPageLanguageTextColor
                    }
                    landingPageSelectedLanguageTextColor={
                      formData.landingPageSelectedLanguageTextColor
                    }
                    landingPageLanguageButtonBorderColor={
                      formData.landingPageLanguageButtonBorderColor
                    }
                    landingPageSelectedLanguageButtonBorderColor={
                      formData.landingPageSelectedLanguageButtonBorderColor
                    }
                    landingPageDeviceIdTextColor={
                      formData.landingPageDeviceIdTextColor
                    }
                    landingPageErrorTextColor={
                      formData.landingPageErrorTextColor
                    }
                    landingPageErrorIconColor={
                      formData.landingPageErrorIconColor
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {selectedTab === 1 && (
                <motion.div
                  key="categoriesPage"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <CategoriesPage
                    categoryBackgroundColor={formData.categoryBackgroundColor}
                    categoryBorderColor={formData.categoryBorderColor}
                    categorySelectedBorderColor={
                      formData.categorySelectedBorderColor
                    }
                    categoryBorderWidth={formData.categoryBorderWidth}
                    viewCartButtonBackgroundColor={
                      formData.viewCartButtonBackgroundColor
                    }
                    viewCartButtonTextColor={formData.viewCartButtonTextColor}
                    viewCartButtonFontSize={formData.viewCartButtonFontSize}
                    categoryTitleColor={formData.categoryTitleColor}
                    categorySelectedBorderWidth={
                      formData.categorySelectedBorderWidth
                    }
                    kioskCategorySelectionColo={
                      formData.kioskCategorySelectionColor
                    }
                    kioskCategoryBackgroundColor={
                      formData.kioskCategoryBackgroundColor
                    }
                    kioskCategoryForegroundColor={
                      formData.kioskCategoryForegroundColor
                    }
                    itemCardBackgroundColor={formData.itemCardBackgroundColor}
                    itemCardBorderColor={formData.itemCardBorderColor}
                    itemCardBorderWidth={formData.itemCardBorderWidth}
                    itemCardBorderRadius={formData.itemCardBorderRadius}
                    itemNameTextColor={formData.itemNameTextColor}
                    itemNameFontSize={formData.itemNameFontSize}
                    itemDescriptionTextColor={formData.itemDescriptionTextColor}
                    itemDescriptionFontSize={formData.itemDescriptionFontSize}
                    itemPriceTextColor={formData.itemPriceTextColor}
                    itemPriceFontSize={formData.itemPriceFontSize}
                    itemImageWidth={formData.itemImageWidth}
                    itemImageHeight={formData.itemImageHeight}
                    itemImageBorderRadius={formData.itemImageBorderRadius}
                    soldOutBackgroundColor={formData.soldOutBackgroundColor}
                    soldOutTextColor={formData.soldOutTextColor}
                    soldOutFontSize={formData.soldOutFontSize}
                    soldOutText={formData.soldOutText}
                    viewCartButtonPadding={formData.viewCartButtonPadding}
                    viewCartButtonBorderRadius={
                      formData.viewCartButtonBorderRadius
                    }
                    viewCartButtonShadowColor={
                      formData.viewCartButtonShadowColor
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {selectedTab === 2 && (
                <motion.div
                  key="itemModal"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <ItemModal
                    drawerBackgroundColor={formData.drawerBackgroundColor}
                    drawerBorderRadius={formData.drawerBorderRadius}
                    drawerCloseButtonBackgroundColor={
                      formData.drawerCloseButtonBackgroundColor
                    }
                    drawerCloseButtonBorderRadius={
                      formData.drawerCloseButtonBorderRadius
                    }
                    drawerCloseButtonIconColor={
                      formData.drawerCloseButtonIconColor
                    }
                    drawerItemTitleColor={formData.drawerItemTitleColor}
                    drawerItemTitleFontSize={formData.drawerItemTitleFontSize}
                    drawerItemPriceColor={formData.drawerItemPriceColor}
                    drawerItemPriceFontSize={formData.drawerItemPriceFontSize}
                    drawerItemDescriptionColor={
                      formData.drawerItemDescriptionColor
                    }
                    drawerItemDescriptionFontSize={
                      formData.drawerItemDescriptionFontSize
                    }
                    drawerSectionTitleColor={formData.drawerSectionTitleColor}
                    drawerSectionTitleFontSize={
                      formData.drawerSectionTitleFontSize
                    }
                    drawerSectionDividerColor={
                      formData.drawerSectionDividerColor
                    }
                    drawerSectionDividerThickness={
                      formData.drawerSectionDividerThickness
                    }
                    drawerRequiredIndicatorColor={
                      formData.drawerRequiredIndicatorColor
                    }
                    drawerRequiredIndicatorFontSize={
                      formData.drawerRequiredIndicatorFontSize
                    }
                    drawerOptionalIndicatorColor={
                      formData.drawerOptionalIndicatorColor
                    }
                    drawerOptionalIndicatorFontSize={
                      formData.drawerOptionalIndicatorFontSize
                    }
                    drawerSoldOutTextColor={formData.drawerSoldOutTextColor}
                    drawerSoldOutBackgroundColor={
                      formData.drawerSoldOutBackgroundColor
                    }
                    drawerSoldOutFontSize={formData.drawerSoldOutFontSize}
                    drawerBottomActionsBorderColor={
                      formData.drawerBottomActionsBorderColor
                    }
                    drawerBottomActionsBackgroundColor={
                      formData.drawerBottomActionsBackgroundColor
                    }
                    drawerAddToCartButtonColor={
                      formData.drawerAddToCartButtonColor
                    }
                    drawerDisabledButtonColor={
                      formData.drawerDisabledButtonColor
                    }
                    drawerAddToCartButtonTextColor={
                      formData.drawerAddToCartButtonTextColor
                    }
                    drawerAddToCartButtonTextFontSize={
                      formData.drawerAddToCartButtonTextFontSize
                    }
                    drawerAddToCartButtonBorderRadius={
                      formData.drawerAddToCartButtonBorderRadius
                    }
                    drawerIncrementButtonColor={
                      formData.drawerIncrementButtonColor
                    }
                    drawerDecrementButtonColor={
                      formData.drawerDecrementButtonColor
                    }
                    drawerQuantityButtonTextColor={
                      formData.drawerQuantityButtonTextColor
                    }
                    drawerQuantityButtonTextFontSize={
                      formData.drawerQuantityButtonTextFontSize
                    }
                    drawerQuantityValueColor={formData.drawerQuantityValueColor}
                    drawerQuantityValueFontSize={
                      formData.drawerQuantityValueFontSize
                    }
                    drawerQuantityButtonSize={formData.drawerQuantityButtonSize}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {selectedTab === 3 && (
                <motion.div
                  key="categoriesPage"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <StoreClosedModal
                  storeClosedModalBackgroundColor={formData.storeClosedModalBackgroundColor}
                  storeClosedModalBorderRadius={formData.storeClosedModalBorderRadius}
                  storeClosedModalPadding={formData.storeClosedModalPadding}
                  storeClosedModalTextColor={formData.storeClosedModalTextColor}
                  storeClosedModalTextSize={formData.storeClosedModalTextSize}
                  storeClosedModalCloseButtonColor={formData.storeClosedModalCloseButtonColor}
                  storeClosedModalCloseButtonSize={formData.storeClosedModalCloseButtonSize}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {selectedTab === 4 && (
                <motion.div
                  key="categoriesPage"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <InactivityModal
                  inactivityModalTextColor={formData.inactivityModalTextColor}
                  inactivityModalBackgroundColor={formData.inactivityModalBackgroundColor}
                  inactivityModalBorderRadius={formData.inactivityModalBorderRadius}
                  inactivityModalPadding={ formData.inactivityModalPadding}
                  inactivityModalTitleColor={formData.inactivityModalTitleColor}
                  inactivityModalTitleSize={formData.inactivityModalTitleSize}
                  inactivityModalTextSize={formData.inactivityModalTextSize}
                  inactivityModalButtonColor={formData.inactivityModalButtonColor}
                  inactivityModalButtonTextColor={formData.inactivityModalButtonTextColor}
                  inactivityModalButtonTextSize={formData.inactivityModalButtonTextSize}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </TabPanels>
        </Tabs>
      </Box>
    </Flex>
  );
}

export default Configuration;
